import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';

// Production API URL - hardcoded for native pilot build reliability
const PRODUCTION_API_URL = 'https://api.obzidn.com/api';
export const API_BASE_URL = Constants.expoConfig?.extra?.apiBaseUrl || PRODUCTION_API_URL;
console.log('[Obzidn Driver] API Base URL:', API_BASE_URL);

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased timeout to 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Cross-platform storage utilities (works on web and mobile)
export const tokenStorage = {
  async setToken(token) {
    try {
      await AsyncStorage.setItem('authToken', token);
    } catch (error) {
      console.error('Error storing auth token:', error);
    }
  },

  async getToken() {
    try {
      return await AsyncStorage.getItem('authToken');
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  },

  async removeToken() {
    try {
      await AsyncStorage.removeItem('authToken');
    } catch (error) {
      console.error('Error removing auth token:', error);
    }
  },

  async setUserData(userData) {
    try {
      await AsyncStorage.setItem('userData', JSON.stringify(userData));
    } catch (error) {
      console.error('Error storing user data:', error);
    }
  },

  async getUserData() {
    try {
      const userData = await AsyncStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  },

  async removeUserData() {
    try {
      await AsyncStorage.removeItem('userData');
    } catch (error) {
      console.error('Error removing user data:', error);
    }
  },
};

// Request interceptor to add auth token + logging
api.interceptors.request.use(
  async (config) => {
    try {
      const token = await tokenStorage.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
    // Skip logging for frequent location updates to reduce noise
    if (!config.url?.includes('/driver/location')) {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`, config.data ? JSON.stringify(config.data).slice(0, 100) : '');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling + logging
api.interceptors.response.use(
  (response) => {
    if (!response.config.url?.includes('/driver/location')) {
      console.log(`[API] ${response.status} ${response.config.url}`, response.data?.success !== undefined ? `success=${response.data.success}` : '');
    }
    return response;
  },
  async (error) => {
    console.error(`[API] ERROR ${error.response?.status || 'NETWORK'} ${error.config?.url}:`, error.response?.data?.message || error.message);
    // On 401, retry once before clearing token (prevents transient errors from logging out)
    if (error.response?.status === 401 && !error.config._retried) {
      error.config._retried = true;
      try {
        const token = await tokenStorage.getToken();
        if (token) {
          error.config.headers.Authorization = `Bearer ${token}`;
          return api(error.config);
        }
      } catch (_) {}
      // Retry also failed — clear token
      await tokenStorage.removeToken();
      await tokenStorage.removeUserData();
    }
    return Promise.reject(error);
  }
);

export default api;
